import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useStudio } from "./StudioContext";
import { StudioPage, DataTable, ConfBar, Drawer, Loading, DBlock, DList, DKV, Badge, cx, confColor } from "./StudioShared";

const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Missions() {
  const { missions } = useStudio();
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [industry, setIndustry] = useState("");

  const focus = params.get("focus");
  useEffect(() => { if (focus) open(focus); /* eslint-disable-next-line */ }, [focus]);

  const open = async (id) => {
    setOpenId(id); setDetail(null);
    try { const { data } = await api.get(`/admin/mission-plans/${id}`); setDetail(data); } catch { setDetail(null); }
  };
  const close = () => { setOpenId(null); if (params.get("focus")) { params.delete("focus"); setParams(params, { replace: true }); } };

  if (!missions) return <StudioPage title="Mission Explorer" question="What are customers trying to accomplish?"><Loading /></StudioPage>;

  const industries = [...new Set(missions.map((m) => m.context?.data?.industry).filter(Boolean))];
  const rows = industry ? missions.filter((m) => m.context?.data?.industry === industry) : missions;
  const d = detail?.document || {};

  return (
    <StudioPage title="Mission Explorer" question="What are customers trying to accomplish?" testid="missions-explorer"
      actions={
        <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="missions-industry-filter"
          className="bg-navy-800 border border-white/10 text-white text-sm px-3 py-2 rounded-sm focus:border-gold focus:outline-none">
          <option value="">All industries ({missions.length})</option>
          {industries.map((i) => <option key={i} value={i}>{titleCase(i)}</option>)}
        </select>
      }>
      <DataTable testid="missions-table" rows={rows} onRowClick={(m) => open(m.id)} rowTestid={(m) => `mission-row-${m.id}`}
        empty="No mission plans yet. They are generated when clients submit the questionnaire."
        columns={[
          { key: "org", label: "Organization", render: (m) => <div><div className="text-white">{m.org_name || "—"}</div><div className="text-slate-500 text-xs truncate max-w-[280px]">{m.document?.headline || ""}</div></div> },
          { key: "ver", label: "Ver", className: "font-mono text-xs", render: (m) => `v${m.version}` },
          { key: "ind", label: "Industry", render: (m) => titleCase(m.context?.data?.industry || "—") },
          { key: "conf", label: "Confidence", render: (m) => <ConfBar value={m.document?.confidence || 0} /> },
          { key: "strat", label: "Strategists", className: "font-mono text-xs text-center", render: (m) => (m.document?.strategists_consulted || []).length },
          { key: "prov", label: "Provider", render: (m) => <span className="capitalize">{m.document?.provider_used || "—"}</span> },
          { key: "date", label: "Created", className: "text-slate-500 text-xs", render: (m) => m.created_at ? new Date(m.created_at).toLocaleDateString() : "—" },
        ]} />

      <Drawer open={!!openId} onClose={close} testid="mission-drawer"
        title={detail?.org_name || "Mission Plan"} subtitle={detail ? `v${detail.version} · ${d.provider_used || "—"}` : ""}>
        {!detail ? <Loading /> : (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={cx("font-mono text-2xl font-light", confColor(d.confidence || 0))}>{Math.round((d.confidence || 0) * 100)}%</span>
              <span className="text-xs text-slate-500">confidence</span>
              <Badge tone="gold">{(d.strategists_consulted || []).length} strategists</Badge>
            </div>
            <div className="space-y-1.5">
              <DKV label="Project" value={detail.project_id} />
              <DKV label="Client" value={detail.client_email} />
              <DKV label="Strategists" value={(d.strategists_consulted || []).join(", ")} />
              <DKV label="Last analysis" value={detail.created_at ? new Date(detail.created_at).toLocaleString() : null} />
            </div>
            {d.headline && <DBlock title="Headline"><p className="text-white text-sm leading-relaxed">{d.headline}</p></DBlock>}
            {(d.executive_summary || d.overview) && <DBlock title="Executive Summary"><p className="text-slate-300 text-sm leading-relaxed">{d.executive_summary || d.overview}</p></DBlock>}
            {d.business_assessment && <DBlock title="Business Assessment"><p className="text-slate-300 text-sm leading-relaxed">{d.business_assessment}</p></DBlock>}
            <DList title="Goals" items={d.goals} />
            <DList title="Opportunities" items={d.opportunities} />
            <DList title="Risks" items={d.risks} />
            <DList title="Recommended Services" items={d.recommended_services} />
            <div className="space-y-1.5">
              <DKV label="Website" value={d.recommended_website} />
              <DKV label="Timeline" value={d.recommended_timeline} />
              <DKV label="Investment" value={d.recommended_investment} />
            </div>
            {d.action_plan?.length > 0 && (
              <DBlock title="Action Plan">
                <ol className="space-y-3">
                  {d.action_plan.map((it, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="shrink-0 w-6 h-6 flex items-center justify-center border border-gold/40 text-gold text-xs font-mono rounded-sm">{i + 1}</span>
                      <div><div className="text-white text-sm">{it.title || it}</div>{it.detail && <div className="text-slate-400 text-sm mt-0.5">{it.detail}</div>}</div>
                    </li>
                  ))}
                </ol>
              </DBlock>
            )}
          </>
        )}
      </Drawer>
    </StudioPage>
  );
}
